import '../../components/common/VideoUpload/VideoUpload.css';
import '../../components/common/PhotoUpload/PhotoUpload.css';
import 'plyr/dist/plyr.css';

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

// plyr 3.x ships a .d.ts that mixes `export =` with `export default`, which
// trips up TypeScript's default-import inference even with esModuleInterop.
// The runtime ESM build (dist/plyr.mjs) exports Plyr as the default, so the
// import is correct at runtime and the class type resolves for usage below.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error -- see comment above
import Plyr from 'plyr';

import CameraRecorder
  from '../../components/common/CameraRecorder/CameraRecorder';
import ConfirmDialog from '../../components/common/ConfirmDialog/ConfirmDialog';
import IconButton from '../../components/common/IconButton/IconButton';
import SpinnerIcon from '../../components/common/SpinnerIcon';
import TrashIcon from '../../components/common/TrashIcon';
import VideoUploadButton
  from '../../components/common/VideoUploadButton/VideoUploadButton';
import { VideoScoreValue } from '../../types/athlete';
import {
  deleteVideoBlob,
  generateDemoVideoUid,
  getVideoBlob,
  putVideoBlob,
} from '../store/videoBlobStore';

export interface DemoVideoUploadProps {
  videoUid?: string;
  disabled?: boolean;
  label?: string;
  compactEmpty?: boolean;
  onVideoChange: (value: VideoScoreValue | null) => void;
}

/**
 * Demo counterpart to VideoUpload.
 *
 * Differences from the real component:
 *   - No Cloudflare Stream. The "upload" writes the captured Blob to
 *     IndexedDB (see videoBlobStore) and simulates progress over a few
 *     seconds so the spinner has visible motion.
 *   - No iframe playback path. The recorded blob is always read back from
 *     IDB into a plain `<video>` element via createObjectURL.
 *   - No checking/processing/ready phase machine. Once the IDB write
 *     completes, the video is immediately playable.
 *
 * Keeps the same `VideoScoreValue` shape (`{ videoUid, uploadedBy }`) so
 * the parent score field is identical between demo and real flows; only
 * the lookup of the bytes differs.
 */

type Phase = 'idle' | 'preview' | 'uploading' | 'ready' | 'removing';

const FAKE_UPLOAD_DURATION_MS = 2400;
const DEMO_COACH_UID = 'demo-coach';

const DemoVideoUpload: React.FC<DemoVideoUploadProps> = ({
  videoUid,
  disabled = false,
  label = 'Athlete video',
  compactEmpty = false,
  onVideoChange,
}) => {
  const [phase, setPhase] = useState<Phase>(() => (videoUid ? 'ready' : 'idle'));
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [currentBlob, setCurrentBlob] = useState<{ blob: Blob; mimeType: string } | null>(null);
  const [aspect, setAspect] = useState<{ width: number; height: number } | null>(null);

  const localUrlRef = useRef<string | null>(null);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const plyrRef = useRef<Plyr | null>(null);

  const commitLocalUrl = (url: string | null): void => {
    if (localUrlRef.current && localUrlRef.current !== url) {
      URL.revokeObjectURL(localUrlRef.current);
    }
    localUrlRef.current = url;
    setLocalUrl(url);
  };

  // Plyr on the recording-preview video when the capture-preview modal opens.
  useEffect(() => {
    if (phase !== 'preview' || !previewVideoRef.current) return;

    if (plyrRef.current) plyrRef.current.destroy();

    plyrRef.current = new Plyr(previewVideoRef.current, {
      controls: ['play', 'play-large', 'progress', 'current-time', 'duration', 'settings', 'mute'],
      autoplay: false,
      autopause: false,
      clickToPlay: true,
      muted: true,
      speed: { selected: 1, options: [0.5, 1, 1.5, 2] },
      settings: ['speed', 'loop'],
      fullscreen: { enabled: false, iosNative: false },
      keyboard: { focused: false, global: false },
      loop: { active: true },
      hideControls: false,
    });

    return () => {
      if (plyrRef.current) {
        plyrRef.current.destroy();
        plyrRef.current = null;
      }
    };
  }, [phase]);

  // Cleanup the object URL on unmount.
  useEffect(() => () => {
    if (localUrlRef.current) URL.revokeObjectURL(localUrlRef.current);
  }, []);

  // When a videoUid is provided (e.g. on first mount with a previously-
  // saved video), pull the blob from IDB and produce an object URL for
  // playback. We only run this if we don't already have a local URL.
  useEffect(() => {
    if (!videoUid) {
      if (phase !== 'uploading') {
        commitLocalUrl(null);
        setAspect(null);
        setPhase('idle');
      }
      return;
    }
    if (localUrlRef.current) {
      // We already have the just-recorded blob URL; nothing to fetch.
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const blob = await getVideoBlob(videoUid);
        if (cancelled) return;
        if (!blob) {
          // Saved video not found in IDB (e.g. cleared storage). Treat as
          // empty so the user can re-record.
          setError('Saved video missing from local storage.');
          setPhase('idle');
          return;
        }
        commitLocalUrl(URL.createObjectURL(blob));
        setPhase('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load video.');
        setPhase('idle');
      }
    })();
    return () => { cancelled = true; };
  }, [videoUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCapture = (blob: Blob, mimeType: string): void => {
    commitLocalUrl(URL.createObjectURL(blob));
    setCameraOpen(false);
    setCurrentBlob({ blob, mimeType });
    setPhase('preview');
  };

  const handleRetake = (): void => {
    setCurrentBlob(null);
    commitLocalUrl(null);
    setCameraOpen(true);
    setPhase('idle');
  };

  const handleUseVideo = (): void => {
    if (!currentBlob) return;
    const { blob } = currentBlob;
    setCurrentBlob(null);
    void startFakeUpload(blob);
  };

  const startFakeUpload = async (blob: Blob): Promise<void> => {
    setPhase('uploading');
    setUploadProgress(0);
    setError(null);

    const uid = generateDemoVideoUid();

    // Animate progress 0 → 100 over FAKE_UPLOAD_DURATION_MS so the spinner
    // shows realistic motion. The actual IDB write happens immediately and
    // is awaited in parallel; we only finish when both are done.
    const progressInterval = window.setInterval(() => {
      setUploadProgress((p) => {
        const next = p + 100 * (100 / FAKE_UPLOAD_DURATION_MS);
        return next >= 99 ? 99 : next;
      });
    }, 100);

    try {
      await Promise.all([
        putVideoBlob(uid, blob),
        new Promise<void>((resolve) => window.setTimeout(resolve, FAKE_UPLOAD_DURATION_MS)),
      ]);
      setUploadProgress(100);
      setPhase('ready');
      onVideoChange({ videoUid: uid, uploadedBy: DEMO_COACH_UID });
    } catch (err) {
      commitLocalUrl(null);
      setPhase('idle');
      setError(err instanceof Error ? err.message : 'Failed to save video.');
    } finally {
      window.clearInterval(progressInterval);
    }
  };

  const handleRemoveConfirmed = (): void => {
    setRemoveConfirmOpen(false);
    const uidToDelete = videoUid;
    commitLocalUrl(null);
    setAspect(null);
    setPhase('removing');
    onVideoChange(null);
    if (uidToDelete) {
      void deleteVideoBlob(uidToDelete).catch(() => { /* best-effort */ });
    }
  };

  const handleLocalVideoMetadata = (e: React.SyntheticEvent<HTMLVideoElement>): void => {
    const v = e.currentTarget;
    if (v.videoWidth && v.videoHeight && !aspect) {
      setAspect({ width: v.videoWidth, height: v.videoHeight });
    }
  };

  const hasVideo = phase !== 'idle' && phase !== 'removing' && phase !== 'preview';
  const busy = phase === 'uploading' || phase === 'removing';

  const aspectStyle: React.CSSProperties = aspect
    ? { aspectRatio: `${aspect.width} / ${aspect.height}` }
    : { aspectRatio: '9 / 16' };

  const rootClass = [
    'photo-upload',
    compactEmpty && !hasVideo ? 'photo-upload--compact-empty' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClass}>
      {label ? <div className="photo-upload-label">{label}</div> : null}

      {!hasVideo ? (
        <div className="photo-upload-container">
          <VideoUploadButton
            className="photo-upload-button"
            onClick={() => setCameraOpen(true)}
            disabled={disabled || busy}
            ariaLabel={label}
          />
        </div>
      ) : (
        <div className="video-upload-shell">
          <div className="video-upload-player-col">
            {localUrl ? (
              <div className="video-upload-player video-upload-player--local" style={aspectStyle}>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  className="video-upload-player-media"
                  src={localUrl}
                  controls
                  playsInline
                  muted
                  preload="metadata"
                  onLoadedMetadata={handleLocalVideoMetadata}
                />
              </div>
            ) : (
              <div className="video-upload-player video-upload-player--skeleton" style={aspectStyle}>
                <SpinnerIcon className="video-upload-spinner video-upload-spinner--spin" />
              </div>
            )}
          </div>

          <div className="photo-upload-side-actions video-upload-side-actions">
            {phase === 'uploading' ? (
              <svg className="video-upload-spinner video-upload-spinner--uploading" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" strokeOpacity="0.2" />
                <circle
                  cx="12" cy="12" r="10" fill="none"
                  stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray="62.83"
                  strokeDashoffset={62.83 * (1 - uploadProgress / 100)}
                  transform="rotate(-90 12 12)"
                  style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                />
              </svg>
            ) : (
              <IconButton
                className="photo-upload-remove"
                aria-label="Remove video"
                title="Remove video"
                onClick={() => setRemoveConfirmOpen(true)}
                disabled={disabled || busy}
              >
                <TrashIcon />
              </IconButton>
            )}
          </div>
        </div>
      )}

      {error ? (
        <div className="video-upload-error" role="alert">{error}</div>
      ) : null}

      {phase === 'preview' && localUrl ? createPortal(
        <div className="video-upload-preview-modal">
          <div className="video-upload-preview-container">
            <div className="video-upload-preview-video-wrapper">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                ref={previewVideoRef}
                className="video-upload-preview-video"
                src={localUrl}
                playsInline
                autoPlay
                disablePictureInPicture
                disableRemotePlayback
                controlsList="nodownload nofullscreen noremoteplayback"
                {...{ 'x-webkit-airplay': 'deny' }}
              />
            </div>
            <div className="video-upload-preview-bar">
              <div
                className="video-upload-preview-action"
                onClick={handleRetake}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRetake(); }}
              >
                Retake
              </div>
              <div
                className="video-upload-preview-action video-upload-preview-action--primary"
                onClick={handleUseVideo}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleUseVideo(); }}
              >
                Use Video
              </div>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}

      <ConfirmDialog
        open={removeConfirmOpen}
        title="Remove video?"
        message="Are you sure you want to remove this video?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleRemoveConfirmed}
        onCancel={() => setRemoveConfirmOpen(false)}
      />

      {cameraOpen ? (
        <CameraRecorder
          quality="max"
          onCapture={handleCapture}
          onClose={() => setCameraOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default DemoVideoUpload;
